/*
 * 组件共用的样式基元与"语义 → 令牌"映射。
 *
 * 这里只放两样东西：
 *
 *   1. `StatusLevel → 令牌类名` 的映射。这是颜色与语义之间**唯一**的连接点：
 *      组件只接受语义等级（ok / caution / critical…），不接受颜色。这样调用方
 *      永远不需要回答"这个情况该用哪个颜色"，换主题、加高对比度时也不会有
 *      某个组件漏改。反过来说：任何组件里出现 `text-red-500` 都是这一层被绕过了。
 *
 *   2. 跨组件共享的类名片段（焦点环、控件高度、输入框外观）。同一套视觉写在
 *      十个文件里，改一次要改十次 —— 而且总会漏掉一个。
 */

/*
 * 等级语义（与 tokens.css 的注释保持一致，不可互换）：
 *   neutral  中性：没有好坏，只是信息
 *   ok       正常 / 已达成
 *   info     中性提示（不是警告）
 *   caution  需要注意，但可以继续
 *   warning  需要立即处理，尚未失控
 *   critical 失控 / 不可逆 / 已违反安全边界
 *
 * 为什么不做 `success / error / danger` 这套命名：`error` 无法区分"超时重试一下"
 * 和"解锁被拒绝"，而地面站里这两件事的处理方式完全不同。
 */
export type StatusLevel = 'neutral' | 'ok' | 'info' | 'caution' | 'warning' | 'critical';

export const STATUS_LEVELS: readonly StatusLevel[] = [
  'neutral',
  'ok',
  'info',
  'caution',
  'warning',
  'critical',
];

export interface StatusTone {
  /** 柔和底色。高对比度主题下 `-soft` 会变成透明，因此必须与 `border` 同时使用 */
  surface: string;
  /** 该等级的前景色 */
  text: string;
  /** 四面描边 */
  border: string;
  /** 仅左侧描边（横幅、通知条：横向提示用左边线比整圈边框更省空间） */
  borderLeft: string;
  /** 实心填充：进度条、状态圆点 */
  fill: string;
}

export const STATUS_TONES: Record<StatusLevel, StatusTone> = {
  neutral: {
    surface: 'bg-surface-3',
    text: 'text-fg-muted',
    border: 'border-line-strong',
    borderLeft: 'border-l-line-strong',
    fill: 'bg-line-strong',
  },
  ok: {
    surface: 'bg-ok-soft',
    text: 'text-ok',
    border: 'border-ok',
    borderLeft: 'border-l-ok',
    fill: 'bg-ok',
  },
  info: {
    surface: 'bg-info-soft',
    text: 'text-info',
    border: 'border-info',
    borderLeft: 'border-l-info',
    fill: 'bg-info',
  },
  caution: {
    surface: 'bg-caution-soft',
    text: 'text-caution',
    border: 'border-caution',
    borderLeft: 'border-l-caution',
    fill: 'bg-caution',
  },
  warning: {
    surface: 'bg-warning-soft',
    text: 'text-warning',
    border: 'border-warning',
    borderLeft: 'border-l-warning',
    fill: 'bg-warning',
  },
  critical: {
    surface: 'bg-critical-soft',
    text: 'text-critical',
    border: 'border-critical',
    borderLeft: 'border-l-critical',
    fill: 'bg-critical',
  },
};

/*
 * 每个等级配一个**形状**不同的字符。
 *
 * 只靠颜色编码等级是不完整的：色觉障碍用户分不出 ok 与 critical，正午户外屏上
 * 饱和度差异也会被冲掉。形状与颜色双通道，任何一条通道失效时都还能读。
 * 这些字符一律 `aria-hidden` —— 等级含义由文字或 `aria-checked` 表达，
 * 不让读屏器念出"黑色方块"。
 */
export const STATUS_GLYPHS: Record<StatusLevel, string> = {
  neutral: '·',
  ok: '✓',
  info: 'i',
  caution: '!',
  warning: '▲',
  critical: '!!',
};

/*
 * 键盘焦点环。
 *
 * 刻意不写 `outline-none` 去换"干净"：焦点是键盘用户唯一能看见的
 * "我现在在哪个控件上"。颜色取 `--focus-ring` 令牌，高对比度主题会换成另一种值，
 * 所以这里不能写具体色值。
 */
export const FOCUS_RING =
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--focus-ring)';

/** 控件高度阶梯。同一行里的按钮、输入框、下拉必须等高，否则看着像没对齐的表单。 */
export const CONTROL_HEIGHT: Record<'sm' | 'md' | 'lg', string> = {
  sm: 'h-7',
  md: 'h-9',
  lg: 'h-11',
};

/*
 * 输入类控件的公共外观。
 *
 * 非法态的样式挂在 `aria-invalid` 变体上，而不是开一个 `invalidClassName` 之类的口子：
 * 这样"边框是红的"和"读屏器说它非法"来自同一个来源，不可能出现
 * "看着是错的、语义上却是正常的"。
 */
export const INPUT_BASE =
  'w-full rounded-control border border-line-strong bg-surface-1 text-sm text-fg placeholder:text-fg-subtle aria-invalid:border-critical disabled:cursor-not-allowed disabled:opacity-50';

/** 拼类名。过滤空值，避免产出 `"a  b"` 这种带空串的 class（调试时很难看） */
export function cx(...parts: ReadonlyArray<string | false | null | undefined>): string {
  return parts.filter((part): part is string => typeof part === 'string' && part !== '').join(' ');
}
